import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Order, Delivery, RemainingQuantitiesResponse, LexwareTransferHistoryEntry } from 'src/app/interfaces/orders';
import { DeliveryService } from 'src/app/services/manager/delivery.service';
import { AlertService } from 'src/app/services/base/alert.service';

/** Vorberechnete Anzeige-Daten fuer eine Zeile in der kompakten Lieferungs-Liste — spart dem
 * Template wiederholte Methodenaufrufe pro Change-Detection-Zyklus (CLAUDE.md Performance). */
interface DeliveryRow {
  delivery: Delivery;
  totalQty: number;
  netPrice: number;
  grossPrice: number;
  history: LexwareTransferHistoryEntry[];
  /** Schlechtester Lexware-Status ueber alle Uebertragungsversuche — fuer die
   * zusammengefasste Statusanzeige in der eingeklappten Zeile. */
  worstLexwareStatus: 'none' | 'pending' | 'failed' | 'done-unconfirmed' | 'done-confirmed';
}

@Component({
  selector: 'app-delivery-manager',
  templateUrl: './delivery-manager.component.html',
  styleUrls: ['./delivery-manager.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryManagerComponent implements OnInit {
  @Input() order!: Order;

  loading = true;
  remainingData: any = null;
  deliveries: Delivery[] = [];

  // Products with editable delivery quantities
  deliveryProducts: any[] = [];

  // View mode: 'overview' | 'create' | 'edit'
  viewMode: 'overview' | 'create' | 'edit' = 'overview';
  
  // Delivery being edited
  editingDelivery: Delivery | null = null;

  // Lexware-Uebertragungshistorie je Delivery-ID, per deliveryId gecacht — Template ruft
  // keine Methode direkt auf, sondern liest aus dieser Map (siehe CLAUDE.md Performance-Regeln).
  lexwareHistoryByDeliveryId = new Map<string, LexwareTransferHistoryEntry[]>();

  // Sofort-Sperre gegen Doppelklick: waehrend eine Uebertragung fuer eine Delivery laeuft
  // (oder kurz danach), ist der Button deaktiviert — verhindert Doppel-Trigger, ohne auf
  // den Server-Roundtrip warten zu muessen. Ergaenzt den serverseitigen Cooldown.
  private readonly SUBMIT_COOLDOWN_MS = 30_000;
  submittingDeliveryIds = new Set<string>();

  // Welche Lieferungs-Zeile ist aufgeklappt (Produktdetails + Lexware-Historie) — nur eine
  // Zeile gleichzeitig, damit die Liste kompakt und scannbar bleibt.
  expandedDeliveryId: string | null = null;

  // Vorberechnete Zeilen-Ansicht (eine Zeile pro Lieferung) — das Template liest nur aus
  // diesem Array statt bei jedem Change-Detection-Zyklus Methoden pro Lieferung aufzurufen.
  deliveryRows: DeliveryRow[] = [];

  constructor(
    private modalController: ModalController,
    private deliveryService: DeliveryService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.loading = true;
    this.cdr.markForCheck();

    const data = await this.deliveryService.getRemainingQuantities(this.order.orderId);
    if (data) {
      this.remainingData = data;
      this.deliveries = data.deliveries || [];
      this.prepareDeliveryProducts(data.products);
      await this.loadLexwareHistories();
    }

    this.loading = false;
    this.cdr.markForCheck();
  }

  /**
   * Prepares editable product list for a new delivery.
   * Each size gets a `deliveryQty` field (default 0) and `maxQty` (remaining).
   */
  private prepareDeliveryProducts(products: any[]) {
    this.deliveryProducts = products.map((p: any) => ({
      ...p,
      sizes: (p.sizes || []).map((s: any) => ({
        ...s,
        maxQty: s.quantity, // remaining quantity
        deliveryQty: 0,
      })),
    }));
  }

  /**
   * Prepares editable product list for editing an existing delivery.
   */
  private prepareEditDeliveryProducts(delivery: Delivery) {
    const deliveryProductMap = new Map();
    
    // Map delivery products by modelId
    delivery.products.forEach((dp: any) => {
      deliveryProductMap.set(dp.modelId, dp);
    });

    // Get remaining quantities and merge with delivery data
    this.deliveryProducts = (this.remainingData?.products || []).map((p: any) => {
      const deliveryProduct = deliveryProductMap.get(p.modelId);
      
      return {
        ...p,
        sizes: (p.sizes || []).map((s: any) => {
          const deliverySize = deliveryProduct?.sizes?.find((ds: any) => ds.size === s.size);
          const currentDeliveryQty = deliverySize?.quantity || 0;
          
          return {
            ...s,
            maxQty: s.quantity + currentDeliveryQty, // remaining + current delivery
            deliveryQty: currentDeliveryQty,
          };
        }),
      };
    });
  }

  // --- View mode ---

  switchToCreate() {
    // Set all deliveryQty to maxQty (remaining quantity) by default
    for (const product of this.deliveryProducts) {
      for (const size of product.sizes) {
        size.deliveryQty = size.maxQty;
      }
    }
    this.viewMode = 'create';
    this.cdr.markForCheck();
  }

  switchToOverview() {
    this.viewMode = 'overview';
    this.editingDelivery = null;
    this.cdr.markForCheck();
  }

  async confirmEdit(delivery: Delivery) {
    const confirmed = await this.alert.presentConfirmationAlert(
      'Lieferung bearbeiten',
      'Möchten Sie diese Lieferung wirklich bearbeiten? Bereits erstellte Rechnungen werden dadurch nicht automatisch aktualisiert.'
    );
    
    if (confirmed) {
      this.switchToEdit(delivery);
    }
  }

  switchToEdit(delivery: Delivery) {
    this.editingDelivery = delivery;
    this.viewMode = 'edit';
    this.prepareEditDeliveryProducts(delivery);
    this.cdr.markForCheck();
  }

  // --- Quantity editing ---

  incrementQty(size: any) {
    if (size.deliveryQty < size.maxQty) {
      size.deliveryQty++;
      this.cdr.markForCheck();
    }
  }

  decrementQty(size: any) {
    if (size.deliveryQty > 0) {
      size.deliveryQty--;
      this.cdr.markForCheck();
    }
  }

  updateQty(size: any, value: any) {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) {
      size.deliveryQty = 0;
    } else if (parsed > size.maxQty) {
      size.deliveryQty = size.maxQty;
    } else {
      size.deliveryQty = parsed;
    }
    this.cdr.markForCheck();
  }

  setAllRemaining() {
    for (const product of this.deliveryProducts) {
      for (const size of product.sizes) {
        size.deliveryQty = size.maxQty;
      }
    }
    this.cdr.markForCheck();
  }

  resetAll() {
    for (const product of this.deliveryProducts) {
      for (const size of product.sizes) {
        size.deliveryQty = 0;
      }
    }
    this.cdr.markForCheck();
  }

  getTotalDeliveryQty(): number {
    let total = 0;
    for (const product of this.deliveryProducts) {
      for (const size of product.sizes) {
        total += size.deliveryQty || 0;
      }
    }
    return total;
  }

  getProductDeliveryQty(product: any): number {
    return (product.sizes || []).reduce(
      (sum: number, s: any) => sum + (s.deliveryQty || 0),
      0
    );
  }

  hasDeliveryItems(): boolean {
    return this.getTotalDeliveryQty() > 0;
  }

  // --- Create delivery ---

  async submitDelivery() {
    if (!this.hasDeliveryItems()) {
      this.alert.presentToast('Keine Artikel mit Menge > 0 ausgewählt');
      return;
    }

    // Build products payload: only include products/sizes with deliveryQty > 0
    const products = this.deliveryProducts
      .map((p: any) => ({
        ...p,
        sizes: (p.sizes || [])
          .filter((s: any) => (s.deliveryQty || 0) > 0)
          .map((s: any) => ({
            size: s.size,
            quantity: s.deliveryQty,
            override: s.override ?? null,
            blocked: s.blocked ?? false,
          })),
      }))
      .filter((p: any) => p.sizes.length > 0);

    const result = await this.deliveryService.createDelivery(this.order.orderId, products);
    if (result) {
      // Reload data to reflect new delivery
      await this.loadData();
      this.viewMode = 'overview';
      this.cdr.markForCheck();
    }
  }

  async updateDelivery() {
    if (!this.editingDelivery || !this.hasDeliveryItems()) {
      this.alert.presentToast('Keine Artikel mit Menge > 0 ausgewählt');
      return;
    }

    const confirmed = await this.alert.presentConfirmationAlert(
      'Lieferung aktualisieren',
      'Möchten Sie die Änderungen an dieser Lieferung speichern?'
    );
    if (!confirmed) return;

    // Build products payload
    const products = this.deliveryProducts
      .map((p: any) => ({
        ...p,
        sizes: (p.sizes || [])
          .filter((s: any) => (s.deliveryQty || 0) > 0)
          .map((s: any) => ({
            size: s.size,
            quantity: s.deliveryQty,
            override: s.override ?? null,
            blocked: s.blocked ?? false,
          })),
      }))
      .filter((p: any) => p.sizes.length > 0);

    const result = await this.deliveryService.updateDelivery(
      this.editingDelivery.id,
      products
    );
    
    if (result) {
      await this.loadData();
      this.viewMode = 'overview';
      this.editingDelivery = null;
      this.cdr.markForCheck();
    }
  }

  // --- Delivery invoice ---

  isSubmittingInvoice(deliveryId: string): boolean {
    return this.submittingDeliveryIds.has(deliveryId);
  }

  async createDeliveryInvoice(delivery: Delivery) {
    if (this.submittingDeliveryIds.has(delivery.id)) {
      this.alert.presentToast('Übertragung läuft bereits — bitte kurz warten');
      return;
    }

    if (delivery.lexwareQueueCount) {
      const historyText = await this.deliveryService.getTransferHistoryText(delivery.id);
      const message = historyText
        ? `Lieferung ${delivery.deliveryIndex} wurde bereits ${delivery.lexwareQueueCount}x an Lexware übermittelt:\n\n${historyText}\n\nTrotzdem erneut senden?`
        : `Lieferung ${delivery.deliveryIndex} wurde bereits ${delivery.lexwareQueueCount}x an Lexware übermittelt. Trotzdem erneut senden?`;
      const confirmedDuplicate = await this.alert.presentConfirmationAlert('Bereits gesendet', message);
      if (!confirmedDuplicate) return;
    }

    const noCustomer = !this.order.customerAddress;
    const noProducts = !delivery.products || delivery.products.length === 0;

    if (noCustomer || noProducts) {
      const reason = noCustomer && noProducts
        ? 'kein Kunde ausgewählt und keine Produkte vorhanden'
        : noCustomer
          ? 'kein Kunde ausgewählt'
          : 'keine Produkte vorhanden';
      const confirmed = await this.alert.presentConfirmationAlert(
        'Unvollständige Rechnungsdaten',
        `Für diese Lieferung ist ${reason}. Trotzdem eine Rechnung erstellen?`
      );
      if (!confirmed) return;
    }

    const confirmed = await this.alert.presentConfirmationAlert(
      'Rechnung in Lexware-Warteschlange',
      `Lieferung ${delivery.deliveryIndex} zur Lexware-Verarbeitung einreihen?`
    );
    if (!confirmed) return;

    this.submittingDeliveryIds.add(delivery.id);
    this.cdr.markForCheck();

    try {
      const success = await this.deliveryService.queueDeliveryInvoiceForLexware(delivery);
      if (success) {
        await this.loadData();
      }
    } finally {
      // Sperre bleibt kurz ueber den Request hinaus bestehen (nicht sofort geloest) — das
      // deckt den Fall ab, dass der Nutzer sofort erneut klickt, obwohl der Server den
      // Queue-Eintrag noch verarbeitet.
      setTimeout(() => {
        this.submittingDeliveryIds.delete(delivery.id);
        this.cdr.markForCheck();
      }, this.SUBMIT_COOLDOWN_MS);
    }
  }

  async createDeliveryNote(delivery: Delivery) {
    const confirmed = await this.alert.presentConfirmationAlert(
      'Lieferschein erstellen',
      `Lieferschein für Lieferung ${delivery.deliveryIndex} erstellen?`
    );
    if (!confirmed) return;

    await this.deliveryService.createDeliveryNote(this.order, delivery);
  }

  // --- Helpers ---

  getDeliveryTotalQty(delivery: Delivery): number {
    return this.deliveryService.calculateTotalQuantity(delivery.products);
  }

  getInvoiceStatusLabel(status: string): string {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'invoiced': return 'Rechnung erstellt';
      case 'paid': return 'Bezahlt';
      default: return status;
    }
  }

  getInvoiceStatusColor(status: string): string {
    switch (status) {
      case 'pending': return 'warning';
      case 'invoiced': return 'primary';
      case 'paid': return 'success';
      default: return 'medium';
    }
  }

  trackByModelId(index: number, item: any): string {
    return item?.modelId || index.toString();
  }

  trackBySize(index: number, item: any): string {
    return item?.size || index.toString();
  }

  trackByDeliveryId(index: number, item: Delivery): string {
    return item?.id || index.toString();
  }

  trackByDeliveryRow(index: number, row: DeliveryRow): string {
    return row?.delivery?.id || index.toString();
  }

  /** Laedt die Lexware-Uebertragungshistorie fuer alle sichtbaren Lieferungen parallel und
   * baut daraus deliveryRows — die einzige Datenquelle, die das Listen-Template liest. */
  private async loadLexwareHistories(): Promise<void> {
    const results = await Promise.all(
      this.deliveries.map((d) => this.deliveryService.getTransferHistory(d.id)),
    );
    this.lexwareHistoryByDeliveryId.clear();
    this.deliveries.forEach((d, i) => this.lexwareHistoryByDeliveryId.set(d.id, results[i]));
    this.rebuildDeliveryRows();
  }

  private rebuildDeliveryRows(): void {
    this.deliveryRows = this.deliveries.map((delivery) => {
      const history = this.lexwareHistoryByDeliveryId.get(delivery.id) || [];
      return {
        delivery,
        totalQty: this.getDeliveryTotalQty(delivery),
        netPrice: this.getDeliveryTotalPrice(delivery),
        grossPrice: this.getDeliveryGrossPrice(delivery),
        history,
        worstLexwareStatus: this.computeWorstLexwareStatus(history),
      };
    });
  }

  private computeWorstLexwareStatus(history: LexwareTransferHistoryEntry[]): DeliveryRow['worstLexwareStatus'] {
    if (!history.length) return 'none';
    // Neuester Eintrag zaehlt (history ist bereits -updatedAt sortiert vom Backend).
    const latest = history[0];
    if (latest.status === 'failed') return 'failed';
    if (latest.status === 'pending') return 'pending';
    return latest.lexwareConfirmedAt ? 'done-confirmed' : 'done-unconfirmed';
  }

  getLexwareHistory(deliveryId: string): LexwareTransferHistoryEntry[] {
    return this.lexwareHistoryByDeliveryId.get(deliveryId) || [];
  }

  toggleDeliveryExpanded(deliveryId: string): void {
    this.expandedDeliveryId = this.expandedDeliveryId === deliveryId ? null : deliveryId;
    this.cdr.markForCheck();
  }

  isDeliveryExpanded(deliveryId: string): boolean {
    return this.expandedDeliveryId === deliveryId;
  }

  rowStatusLabel(status: DeliveryRow['worstLexwareStatus']): string {
    switch (status) {
      case 'none': return 'Nicht gesendet';
      case 'pending': return 'Ausstehend';
      case 'failed': return 'Fehlgeschlagen';
      case 'done-unconfirmed': return 'Unbestätigt';
      case 'done-confirmed': return 'Bestätigt';
    }
  }

  rowStatusColor(status: DeliveryRow['worstLexwareStatus']): string {
    switch (status) {
      case 'none': return 'medium';
      case 'pending': return 'warning';
      case 'failed': return 'danger';
      case 'done-unconfirmed': return 'medium';
      case 'done-confirmed': return 'success';
    }
  }

  lexwareStatusLabel(status: string): string {
    switch (status) {
      case 'done': return 'In Lexware angekommen';
      case 'failed': return 'Fehlgeschlagen';
      default: return 'Ausstehend';
    }
  }

  lexwareStatusColor(status: string): string {
    switch (status) {
      case 'done': return 'success';
      case 'failed': return 'danger';
      default: return 'warning';
    }
  }

  async confirmLexwareEntry(deliveryId: string, entry: LexwareTransferHistoryEntry): Promise<void> {
    const success = await this.deliveryService.confirmLexwareEntrySeen(entry);
    if (!success) return;

    const history = this.lexwareHistoryByDeliveryId.get(deliveryId) || [];
    const updated = history.map((h) =>
      h.id === entry.id
        ? { ...h, lexwareConfirmedAt: new Date().toISOString() }
        : h,
    );
    this.lexwareHistoryByDeliveryId.set(deliveryId, updated);
    this.rebuildDeliveryRows();
    this.cdr.markForCheck();
  }

  hasQuantity(product: any): boolean {
    if (!product?.sizes || !Array.isArray(product.sizes)) {
      return false;
    }
    return product.sizes.some((size: any) => (size?.quantity || 0) > 0);
  }

  getDeliveryTotalPrice(delivery: Delivery): number {
    if (!delivery?.products || !Array.isArray(delivery.products)) {
      return 0;
    }
    
    let total = 0;
    for (const product of delivery.products) {
      const price = product?.price || 0;
      const sizes = product?.sizes || [];
      
      for (const size of sizes) {
        const quantity = size?.quantity || 0;
        total += price * quantity;
      }
    }
    
    return total;
  }

  getDeliveryGrossPrice(delivery: Delivery): number {
    const netPrice = this.getDeliveryTotalPrice(delivery);
    return netPrice * 1.19; // 19% MwSt
  }

  close() {
    this.modalController.dismiss();
  }
}
